import ReactPlayer from 'react-player';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

/**
 * @example
 * <VideoPlayer src="https://.../video.mp4" poster="/img/poster.png" controls />
 *
 * @param {import('./lib/propTypes').VideoPlayerProps} props
 */
const VideoPlayer = forwardRef(function VideoPlayer(
  {
    src,
    poster,
    autoPlay = false,
    muted = false,
    controls = true,
    loop = false,
    className = '',
    style = {},
    onAnalytics = () => {},
    width = '100%',
    height = '100%',
    preload = 'auto',
    playsInline = true,
    children
  },
  ref
) {
  const playerRef = useRef(null);
  const visibleRef = useRef(true);
  const watchingRef = useRef(false);
  const lastPerfRef = useRef(null);
  const watchedMsRef = useRef(0);
  // Latest-callback ref: the heartbeat interval and visibility listeners are set up
  // once (empty deps), so without this they would keep calling the onAnalytics
  // closure from the first render
  const onAnalyticsRef = useRef(onAnalytics);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    onAnalyticsRef.current = onAnalytics;
  }, [onAnalytics]);

  // expose a simple imperative handle so parent components can call player functions
  useImperativeHandle(
    ref,
    () => ({
      getInternalPlayer: () => playerRef.current,
      snapshot: (extra = {}) => snapshot(extra)
    }),
    []
  );

  // Tab visibility tracking
  useEffect(() => {
    const onVis = () => {
      visibleRef.current = document.visibilityState === 'visible';
      emit('visibility', { visible: visibleRef.current });
    };
    onVis();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    window.addEventListener('blur', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
      window.removeEventListener('blur', onVis);
    };
  }, []);

  // Precise watch-time accumulation using requestAnimationFrame
  useEffect(() => {
    let rafId;
    const step = () => {
      const player = playerRef.current;
      if (player && watchingRef.current && visibleRef.current) {
        const now = performance.now();
        if (lastPerfRef.current != null) {
          watchedMsRef.current += now - lastPerfRef.current;
        }
        lastPerfRef.current = now;
      } else {
        lastPerfRef.current = null;
      }
      rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // Heartbeat every 10s + unload beacon
  useEffect(() => {
    const id = setInterval(() => emit('heartbeat'), 10000);
    const unload = () => beacon('unload');
    window.addEventListener('beforeunload', unload);
    return () => {
      clearInterval(id);
      window.removeEventListener('beforeunload', unload);
    };
  }, []);

  function snapshot(extra = {}) {
    const player = playerRef.current;
    // react-player v3 forwards an HTMLVideoElement-like ref (currentTime/duration are
    // properties); the method form is kept as a fallback for the v2 API
    const currentTime =
      Number(
        typeof player?.getCurrentTime === 'function' ? player.getCurrentTime() : player?.currentTime
      ) || 0;
    const duration =
      Number(typeof player?.getDuration === 'function' ? player.getDuration() : player?.duration) ||
      0;
    return {
      currentTime,
      duration,
      watchedMs: Math.round(watchedMsRef.current),
      visible: visibleRef.current,
      ts: Date.now(),
      ...extra
    };
  }

  function emit(type, extra = {}) {
    try {
      onAnalyticsRef.current({ type, ...snapshot(extra) });
    } catch (e) {
      // Swallow analytics errors to avoid breaking playback
      // eslint-disable-next-line no-console
      console.error('VideoPlayer analytics error', e);
    }
  }

  function beacon(type, extra = {}) {
    try {
      const payload = JSON.stringify({ type, ...snapshot(extra) });
      navigator.sendBeacon?.('/analytics/video', payload);
    } catch (e) {
      // ignore
    }
  }

  return (
    <div
      className={`aspect-video w-full max-w-4xl overflow-hidden rounded-2xl ${className}`}
      style={style}
      aria-hidden={false}>
      <ReactPlayer
        ref={playerRef}
        src={src}
        autoPlay={autoPlay}
        muted={muted}
        controls={controls}
        loop={loop}
        width={width}
        height={height}
        poster={poster}
        preload={preload}
        playsInline={playsInline}
        onLoadedMetadata={() => {
          setReady(true);
          emit('ready');
        }}
        onPlay={() => {
          watchingRef.current = true;
          emit('play');
        }}
        onPause={() => {
          watchingRef.current = false;
          emit('pause');
        }}
        onEnded={() => {
          watchingRef.current = false;
          emit('ended');
        }}
        onTimeUpdate={() => ready && emit('timeupdate')}
        onSeeked={() => emit('seek')}
        onWaiting={() => emit('buffer')}
        onPlaying={() => emit('bufferend')}
        onError={(e) => emit('error', { error: String(e) })}
      />
      {children}
    </div>
  );
});

export default VideoPlayer;
